import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'src/hooks/local-storage';
import { ViewerMode } from 'src/components/common/viewer-mode';
import AggregatedView = Autodesk.Viewing.AggregatedView;

type UsePdfOpacityProps = {
  aggregatedView: AggregatedView | null;
  viewerMode: ViewerMode;
  sheetId: string | null;
  pageNumber: number;
};

export function usePdfOpacity({
  aggregatedView,
  viewerMode,
  sheetId,
  pageNumber,
}: UsePdfOpacityProps) {
  const [opacityStore, setOpacityStore] = useLocalStorage<
    Record<string, Record<number, number>>
  >('sheet-pdf-opacity-v2', {});

  const storageKey = sheetId ?? 'default';
  const opacity = (opacityStore[storageKey]?.[pageNumber] ?? 80) as number;
  const setOpacity = (value: number) => {
    setOpacityStore((prev) => {
      const sheetMap = prev[storageKey] ?? {};
      return {
        ...prev,
        [storageKey]: {
          ...sheetMap,
          [pageNumber]: value,
        },
      };
    });
  };

  const findMaterialManager = useCallback((viewerImpl: any) => {
    try {
      const matman = (viewerImpl as any).matman;
      if (matman && typeof matman === 'function') {
        const materialManager = matman();
        if (
          materialManager &&
          (materialManager._materials || materialManager.materials)
        ) {
          return { materialManager, path: 'matman()' };
        }
      }
    } catch (error) {
      // ignore error
    }

    return { materialManager: null, path: null };
  }, []);

  const modifyPDFMaterials = useCallback((materials: any, opacity: number) => {
    let pdfMaterialsModified = 0;
    const opacityValue = opacity / 100;

    if (opacity >= 99.9) {
      return 0;
    }

    Object.entries(materials).forEach(([key, material]) => {
      // Check if this is a PDF material (contains "model:", where N is a number) and not a shape material
      if (key.includes('model:') && !key.includes('model:|')) {
        if (material && typeof (material as any).opacity !== 'undefined') {
          // Apply opacity to all PDF materials - this is the original behavior
          // The PDF extension should handle model switching properly
          (material as any).opacity = opacityValue;
          (material as any).visible = true;

          if ((material as any).uniforms) {
            const uniforms = (material as any).uniforms;

            const opacityUniforms = [
              'currentOpacity',
              'opacity',
              'alpha',
              'transparent',
              'hasOpacity',
              'materialOpacity',
            ];

            opacityUniforms.forEach((uniformName) => {
              if (uniforms[uniformName] !== undefined) {
                if (uniforms[uniformName].value !== undefined) {
                  uniforms[uniformName].value = opacityValue;
                } else {
                  uniforms[uniformName] = opacityValue;
                }
              }
            });
          }

          (material as any).needsUpdate = true;
          (material as any).transparent = true;

          pdfMaterialsModified++;
        }
      }
    });

    return pdfMaterialsModified;
  }, []);

  const modifyRendererSettings = useCallback(
    (materialManager: any, opacity: number) => {
      if (opacity >= 99.9) {
        return;
      }

      const renderer = materialManager._renderer;
      if (renderer) {
        if (renderer.setClearAlpha) {
          renderer.setClearAlpha(opacity / 100);
        }

        if (renderer.state) {
          renderer.state.blending = 'normal';
        }
      }
    },
    []
  );

  const forceViewerReRender = useCallback((viewer: any) => {
    try {
      viewer.impl.invalidate(true, true, true);
    } catch (error) {
      // ignore error
    }
  }, []);

  const applyPDFOpacity = useCallback(
    (opacityValue: number) => {
      if (!aggregatedView?.viewer || !aggregatedView.viewer.impl) {
        return;
      }

      const { materialManager } = findMaterialManager(
        aggregatedView.viewer.impl
      );

      try {
        if (materialManager) {
          const materials =
            materialManager._materials ||
            materialManager.materials ||
            materialManager.getMaterials?.();

          if (materials) {
            // Add a small delay to ensure PDF extension has time to unload previous model
            // This addresses the Chrome-specific race condition
            setTimeout(() => {
              const pdfMaterialsModified = modifyPDFMaterials(
                materials,
                opacityValue
              );

              if (pdfMaterialsModified > 0) {
                forceViewerReRender(aggregatedView.viewer);
                modifyRendererSettings(materialManager, opacityValue);
              } else {
                forceViewerReRender(aggregatedView.viewer);
              }
            }, 50); // Small delay to allow PDF extension to complete model switching
          }
        }
      } catch (error) {
        // ignore error
      }
    },
    [
      aggregatedView?.viewer,
      findMaterialManager,
      modifyPDFMaterials,
      modifyRendererSettings,
      forceViewerReRender,
    ]
  );

  useEffect(() => {
    if (aggregatedView?.viewer && viewerMode === ViewerMode.SheetsV2) {
      applyPDFOpacity(opacity);
    }
  }, [aggregatedView, viewerMode, opacity, applyPDFOpacity]);

  useEffect(() => {
    const viewer = aggregatedView?.viewer;
    if (viewer && viewerMode === ViewerMode.SheetsV2) {
      const onGeometryLoaded = () => {
        applyPDFOpacity(opacity);
      };

      viewer.addEventListener(
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
        onGeometryLoaded
      );

      return () => {
        viewer.removeEventListener(
          Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
          onGeometryLoaded
        );
      };
    }
  }, [aggregatedView, viewerMode, opacity, applyPDFOpacity]);

  return {
    opacity,
    setOpacity,
  };
}
